import { useMemo } from "react"
import { Card, ErrorNote, Spinner, useLoad } from "../components/ui"
import { api } from "../lib/api"

// Where employees get Moderniser. Rendered as a section of the public docs
// page (/docs#downloads) — the person installing may not have an account yet.
//
// The version and the file list come from /api/downloads rather than being
// written here, so shipping a release is an env change on the server and not a
// frontend deploy. The links point at that same endpoint, which redirects to
// the CDN — nobody sees a bucket hostname, and the URL survives the bucket
// moving.

type Downloads = {
  version: string
  targets: string[]
  files: Record<string, string>
}

type Target = { id: string; label: string; hint?: string }

type Platform = {
  id: "mac" | "windows" | "linux"
  label: string
  targets: Target[]
  // What the first launch does that looks like a failure but is not.
  gatekeeper?: React.ReactNode
}

const PLATFORMS: Platform[] = [
  {
    id: "mac",
    label: "macOS",
    targets: [
      { id: "mac-arm64", label: "Apple Silicon", hint: "M1 and later" },
      { id: "mac-x64", label: "Intel" },
    ],
    gatekeeper: (
      <>
        macOS will say the app is <strong>"damaged"</strong>. It is not — the build simply
        is not signed yet. Right-click the app and choose <strong>Open</strong> the first
        time, or run <code className="font-mono text-xs">xattr -cr /Applications/Moderniser.app</code>.
      </>
    ),
  },
  {
    id: "windows",
    label: "Windows",
    targets: [
      { id: "win-x64", label: "Intel or AMD", hint: "most PCs" },
      { id: "win-arm64", label: "ARM" },
    ],
    gatekeeper: (
      <>
        Windows will show a SmartScreen warning, because the build is not signed yet.
        Choose <strong>More info</strong>, then <strong>Run anyway</strong>.
      </>
    ),
  },
  {
    id: "linux",
    label: "Linux",
    targets: [
      { id: "linux-appimage-x64", label: "AppImage", hint: "x64 — runs anywhere" },
      { id: "linux-deb-x64", label: ".deb", hint: "x64 — Debian, Ubuntu" },
      { id: "linux-rpm-x64", label: ".rpm", hint: "x64 — Fedora, RHEL" },
      { id: "linux-appimage-arm64", label: "AppImage", hint: "arm64" },
      { id: "linux-deb-arm64", label: ".deb", hint: "arm64" },
      { id: "linux-rpm-arm64", label: ".rpm", hint: "arm64" },
    ],
  },
]

// Which platform to show first. Deliberately does not try to guess the
// processor: on macOS every browser still reports an Intel user-agent on Apple
// Silicon, so a guess would send half of Mac users to the wrong build. The OS
// is reliable, so we get them to the right section and let them pick the chip.
function detectPlatform(): Platform["id"] {
  const ua = navigator.userAgent
  if (/Mac|iPhone|iPad/.test(ua)) return "mac"
  if (/Win/.test(ua)) return "windows"
  return "linux"
}

export function DownloadsSection() {
  const downloads = useLoad(() => api.get<Downloads>("/downloads"))
  const detected = useMemo(detectPlatform, [])

  const ordered = useMemo(
    () => [...PLATFORMS].sort((a, b) => Number(b.id === detected) - Number(a.id === detected)),
    [detected],
  )

  return (
    <>
      <div id="downloads" className="flex flex-col gap-1 scroll-mt-6">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Downloads</h2>
          {downloads.data && <span className="text-xs text-muted">Version {downloads.data.version}</span>}
        </div>
        <p className="text-sm text-muted">
          Moderniser runs in your terminal and on your desktop. Sign in once per machine and both work.
        </p>
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-foreground">The CLI</h2>
        <Card className="flex flex-col gap-3 p-5">
          <pre className="overflow-x-auto rounded-md bg-surface-muted p-4 font-mono text-sm text-foreground">
            <code>
              npm install -g moderniser-ai{"\n"}
              moderniser providers login
            </code>
          </pre>
          <p className="max-w-prose text-sm text-muted">
            The package is <code className="font-mono text-xs">moderniser-ai</code>; the command
            it installs is <code className="font-mono text-xs">moderniser</code>.{" "}
            <code className="font-mono text-xs">providers login</code> prints a link with a short
            code in it. Open it, check the code matches what your terminal shows, and approve.
          </p>
          <p className="max-w-prose text-sm text-muted">
            There is nothing to configure — the defaults already point at the right place.
          </p>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-foreground">The desktop app</h2>

        {downloads.loading && <Spinner label="Loading downloads" />}
        <ErrorNote error={downloads.error} />

        {downloads.data && (
          <div className="flex flex-col gap-4">
            {ordered.map((platform) => {
              const isDetected = platform.id === detected
              return (
                <Card key={platform.id} className="flex flex-col gap-4 p-5">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-foreground">{platform.label}</h3>
                    {isDetected && (
                      <span className="rounded-full border border-accent/30 bg-accent-soft px-2 py-0.5 text-xs text-accent">
                        Your system
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {platform.targets.map((target) => (
                      <DownloadLink
                        key={target.id}
                        target={target}
                        emphasis={isDetected ? "primary" : "ghost"}
                      />
                    ))}
                  </div>

                  {platform.gatekeeper && (
                    <p className="max-w-prose text-sm text-muted">{platform.gatekeeper}</p>
                  )}
                </Card>
              )
            })}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-foreground">Signing in</h2>
        <Card className="p-5">
          <p className="max-w-prose text-sm text-muted">
            The CLI and the desktop app read the same credential, so signing in once per machine
            covers both. That one credential authorises the agent's model calls as well as its
            access to your board and the Context Hub — so signing out anywhere revokes it
            everywhere.
          </p>
        </Card>
      </section>
    </>
  )
}

// Button in ui.tsx renders a <button> and takes no `as` prop. A download must be
// an anchor — the browser only treats a navigation as a download when it is one,
// and a button with an onClick loses middle-click, right-click "save as", and
// the progress UI. So this mirrors Button's classes rather than reusing it.
const EMPHASIS = {
  primary: "bg-accent text-accent-foreground hover:bg-accent/90",
  ghost: "border border-border bg-surface text-foreground hover:bg-surface-muted",
} as const

function DownloadLink({
  target,
  emphasis,
}: {
  target: Target
  emphasis: keyof typeof EMPHASIS
}) {
  return (
    <a
      href={`/api/downloads/${target.id}`}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive ${EMPHASIS[emphasis]}`}
    >
      {target.label}
      {target.hint && <span className="text-xs opacity-70">{target.hint}</span>}
    </a>
  )
}
